import axiosInstance from './axiosInstance';

const API_URL = '/api/v1/support/tickets';

// ── Types ────────────────────────────────────────────────────────────

export interface TicketAttachment {
  id: string;
  message_id: string;
  file_name: string;
  content_type: string;
  file_size: number;
  created_at: string;
}

export interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string | null;
  sender_name: string | null;
  sender_role: string | null;
  content: string;
  is_internal_note: boolean;
  is_system_message: boolean;
  attachments: TicketAttachment[];
  created_at: string;
}

export interface Ticket {
  id: string;
  user_id: string;
  creator_name: string | null;
  creator_email: string | null;
  assigned_to: string | null;
  assignee_name: string | null;
  status: string;
  priority: string;
  category: string | null;
  preview: string | null;
  message_count: number;
  unread_count?: number;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
}

export interface TicketDetail {
  id: string;
  user_id: string;
  creator_name: string | null;
  creator_email: string | null;
  assigned_to: string | null;
  assignee_name: string | null;
  status: string;
  priority: string;
  category: string | null;
  preview?: string | null;
  messages: TicketMessage[];
  unread_count?: number;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
}

export interface TicketListResponse {
  tickets: Ticket[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface SupportNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface SupportNotificationListResponse {
  notifications: SupportNotification[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const extractTicketIdFromNotification = (notification: SupportNotification): string | null => {
  const dataTicketId = notification.data?.ticket_id;

  if (typeof dataTicketId === 'string' && dataTicketId.trim()) {
    return dataTicketId;
  }

  const match = notification.url.match(/\/support\/tickets\/([^/?#]+)/);

  return match?.[1] || null;
};

const fetchUnreadNotificationsPage = async (page: number): Promise<SupportNotificationListResponse> => {
  const response = await axiosInstance.get(`${API_URL}/notifications`, {
    params: {
      page,
      per_page: 100,
      unread_only: true,
    },
  });

  return response.data;
};

const addUnreadNotificationCounts = (
  countsByTicketId: Map<string, number>,
  notifications: SupportNotification[]
) => {
  notifications.forEach((notification) => {
    const ticketId = extractTicketIdFromNotification(notification);

    if (!ticketId) return;

    countsByTicketId.set(ticketId, (countsByTicketId.get(ticketId) || 0) + 1);
  });
};

const getUnreadCountsByTicket = async (
  page = 1,
  countsByTicketId = new Map<string, number>()
): Promise<Map<string, number>> => {
  const data = await fetchUnreadNotificationsPage(page);

  addUnreadNotificationCounts(countsByTicketId, data.notifications);

  if (page >= (data.total_pages || 1)) return countsByTicketId;

  return getUnreadCountsByTicket(page + 1, countsByTicketId);
};

// ── Service ──────────────────────────────────────────────────────────

const ticketDetailPromiseCache = new Map<string, Promise<TicketDetail>>();

export const ticketService = {
  createTicket: async (data: {
    content: string;
    category?: string;
    priority?: string;
  }): Promise<TicketDetail> => {
    const response = await axiosInstance.post(`${API_URL}/`, data);
    return response.data;
  },

  listTickets: async (params: {
    page?: number;
    per_page?: number;
    status?: string;
    priority?: string;
    category?: string;
    search?: string;
    start_date?: string;
    end_date?: string;
    time_zone?: string;
  } = {}): Promise<TicketListResponse> => {
    const response = await axiosInstance.get(`${API_URL}/`, {
      params: {
        ...params,
        time_zone: params.time_zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
    const data: TicketListResponse = response.data;
    const unreadCountsByTicketId = await getUnreadCountsByTicket().catch(() => new Map<string, number>());

    return {
      ...data,
      tickets: data.tickets.map((ticket) => ({
        ...ticket,
        unread_count: Math.max(
          Number(ticket.unread_count ?? 0),
          unreadCountsByTicketId.get(ticket.id) || 0
        ),
      })),
    };
  },

  getTicketDetail: async (ticketId: string, forceRefresh = false): Promise<TicketDetail> => {
    if (!forceRefresh && ticketDetailPromiseCache.has(ticketId)) {
      return ticketDetailPromiseCache.get(ticketId)!;
    }
    const promise = axiosInstance.get(`${API_URL}/${ticketId}`)
      .then((res) => res.data)
      .finally(() => {
        // Clear the cache shortly after the request completes
        // This ensures concurrent requests share the same promise,
        // but future requests (e.g. websocket refreshes) fetch fresh data.
        setTimeout(() => {
          if (ticketDetailPromiseCache.get(ticketId) === promise) ticketDetailPromiseCache.delete(ticketId);
        }, 200);
      });
    ticketDetailPromiseCache.set(ticketId, promise);
    return promise;
  },

  assignTicket: async (
    ticketId: string,
    assignedTo: string
  ): Promise<TicketDetail> => {
    const response = await axiosInstance.put(`${API_URL}/${ticketId}/assign`, {
      assigned_to: assignedTo,
    });
    return response.data;
  },

  updatePriority: async (
    ticketId: string,
    priority: string
  ): Promise<TicketDetail> => {
    const response = await axiosInstance.put(`${API_URL}/${ticketId}/priority`, {
      priority,
    });
    return response.data;
  },

  updateStatus: async (
    ticketId: string,
    status: string
  ): Promise<TicketDetail> => {
    const response = await axiosInstance.put(`${API_URL}/${ticketId}/status`, {
      status,
    });
    return response.data;
  },

  addMessage: async (
    ticketId: string,
    data: { content: string; is_internal_note?: boolean }
  ): Promise<TicketMessage> => {
    const response = await axiosInstance.post(
      `${API_URL}/${ticketId}/messages`,
      data
    );
    ticketDetailPromiseCache.delete(ticketId);
    return response.data;
  },

  uploadAttachments: async (
    ticketId: string,
    messageId: string,
    files: File[],
    onProgress?: (progressPercent: number) => void,
    uploadId?: string
  ): Promise<{ attachments: TicketAttachment[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (uploadId) formData.append('upload_id', uploadId);

    const response = await axiosInstance.post(
      `${API_URL}/${ticketId}/messages/${messageId}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes timeout for video and file uploads
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      }
    );
    ticketDetailPromiseCache.delete(ticketId);
    return response.data;
  },

  downloadAttachment: async (attachmentId: string): Promise<Blob> => {
    const response = await axiosInstance.get(
      `${API_URL}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  getSupportStaff: async (): Promise<SupportStaffUser[]> => {
    const response = await axiosInstance.get(`${API_URL}/staff`);
    return response.data;
  },

  getUnreadNotificationCount: async (): Promise<number> => {
    const response = await axiosInstance.get(`${API_URL}/notifications/unread-count`);
    return response.data?.unread_count ?? 0;
  },

  listNotifications: async (params: {
    page?: number;
    per_page?: number;
    unread_only?: boolean;
  } = {}): Promise<SupportNotificationListResponse> => {
    const response = await axiosInstance.get(`${API_URL}/notifications`, { params });
    return response.data;
  },

  markNotificationRead: async (notificationId: string): Promise<void> => {
    await axiosInstance.put(`${API_URL}/notifications/${notificationId}/read`);
  },

  markAllNotificationsRead: async (): Promise<void> => {
    await axiosInstance.put(`${API_URL}/notifications/read-all`);
  },
};

export interface SupportStaffUser {
  id: string;
  email: string;
  display_name: string;
  system_role: string;
}
